import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import {
  Autocomplete,
  TextField,
  Button,
  Typography,
  Box,
  Container,
  Stack,
} from '@mui/material';
import { useGameUtils } from './utilities/GameUtils';
import { useApi } from './services/api';
import { useAuth } from './contexts/AuthContext';
import { useFirebase } from './contexts/FirebaseContext';
import { Avatar } from '@mui/material';
import WaglLogo from './components/wagl-logo';
import iconLink from './assets/images/icon-link.png';
import iconShare from './assets/images/icon-share-native.png';
import gameCelebrate from './assets/images/game-celebrate.png';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function GameCreated() {
  const navigate = useNavigate();
  const { db } = useFirebase();
  const { id } = useParams();
  const { post } = useApi();
  const { currentUser } = useAuth();
  const [game, setGame] = useState({});
  const [userByUsernameOptions, setUserByUsernameOptions] = useState([]);
  const [invitedUsernameUsers, setInvitedUsernameUsers] = useState([]);
  const [usernameSearch, setUsernameSearch] = useState('');
  const [shareLink, setShareLink] = useState('');
  const shareBaseLink = `${process.env.REACT_APP_WAGL_BASE_URL}/games/`;
  const { state } = useLocation();
  const { newGame } = state || {};
  const debouncedUsernameSearch = useDebounce(usernameSearch, 300);
  const [loading, setLoading] = useState(false);
  const { fetchGameById, fetchAllPreviousPlayers } = useGameUtils();
  const [users, setUsers] = useState([]);

  const loadGame = useCallback(
    async (gameId) => {
      const game = await fetchGameById(gameId);
      if (game) {
        setShareLink(`${shareBaseLink}${game.id}`);
        setGame(game);
      }
    },
    [fetchGameById, shareBaseLink]
  );

  useEffect(() => {
    if (id) {
      loadGame(id);
    }
  }, [id, loadGame]);

  useEffect(() => {
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // const handleSplitPaymentChange = (event) => {
  //   setGame((prevData) => ({
  //     ...prevData,
  //     splitCosts: event.target.checked,
  //   }));
  // };

  // Generic search function
  const searchUsers = useCallback(
    async (searchTerm, setOptions, invitedUsers) => {
      if (!searchTerm || searchTerm.length < 3) {
        setOptions([]);
        return;
      }
      const fields = ['username', 'firstName', 'lastName'];
      try {
        const usersRef = collection(db, 'users');

        // Generate common casing variants
        const lower = searchTerm.toLowerCase();
        const upper = searchTerm.toUpperCase();
        const capitalized = searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase();

        const variants = Array.from(new Set([lower, upper, capitalized])); // remove any duplicates

        // Create multiple queries for each variant

        const queryPromises = fields
          .map((field) =>
            variants.map((variant) =>
              getDocs(
                query(usersRef, where(field, '>=', variant), where(field, '<=', variant + '\uf8ff'))
              )
            )
          )
          .flat();

        const snapshots = await Promise.all(queryPromises);

        // Combine all results and deduplicate by ID
        const usersMap = new Map();
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((doc) => {
            console.log('doc', doc);
            if (!usersMap.has(doc.id)) {
              const user = { id: doc.id, ...doc.data() };
              usersMap.set(doc.id, user);
            }
          });
        });

        // Filter out already invited users
        const users = Array.from(usersMap.values()).filter(
          (user) => !invitedUsers.some((invited) => invited.id === user.id)
        );

        setOptions(users);
      } catch (error) {
        console.error(`Error searching users by: `, error);
      }
    },
    [db]
  );

  const searchUsersByUsername = useCallback(
    (searchTerm) => {
      searchUsers(searchTerm, setUserByUsernameOptions, invitedUsernameUsers);
    },
    [searchUsers, invitedUsernameUsers]
  );

  useEffect(() => {
    searchUsersByUsername(debouncedUsernameSearch);
  }, [debouncedUsernameSearch, searchUsersByUsername]);

  const createInvitations = async () => {
    setLoading(true);
    const invitationsList = invitationsData();

    if (!game?.id) {
      console.error('Game ID is undefined');
      return;
    }

    try {
      // Save the costs before sending invitations.
      const gameRef = doc(db, 'games', game.id);
      await updateDoc(gameRef, game);

      // Invitations are saved in the game document
      // game.invitations = invitationsList;

      // Use the API endpoint to save the invitations
      await post('/invitation/invite-users-to-game', {
        gameID: game.id,
        invitedBy: currentUser.uid,
        userIds: invitationsList,
      });

      navigate(`/games/${game.id}`);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error creating invitations:', error);
      throw error;
    }
  };

  const invitationsData = useCallback(() => {
    return invitedUsernameUsers
      .map((user) => {
        if (!user?.uid) {
          console.error('User has no uid:', user);
          return null;
        }
        return user.uid;
      })
      .filter(Boolean);
  }, [invitedUsernameUsers]);

  useEffect(() => {
    const fetchGames = async () => {
      const previousPlayers = await fetchAllPreviousPlayers(currentUser?.uid);
      setUsers(previousPlayers);
    };

    fetchGames();
  }, [currentUser?.uid, fetchAllPreviousPlayers]);

  const handleUserClick = (user) => {
    setInvitedUsernameUsers((prevInvited) => {
      // Check if the user is already invited
      if (!prevInvited.some((invitedUser) => invitedUser.uid === user.uid)) {
        return [...prevInvited, user];
      }
      return prevInvited;
    });
  };

  const handleShareGameInvite = async (shareLink) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my game on Wagl!',
          text: 'Check out this game invite:',
          url: shareLink,
        });
      } catch (err) {
        // User cancelled or error occurred, do nothing
      }
    } else {
      // fallback: copy to clipboard
      navigator.clipboard.writeText(shareLink);
    }
  };

  // const getUserPhotoURL = (uid) => {
  //       const user = dateTimeOptionsWithPhotoURL?.find (user => user?.uid === uid || user?.user === uid);
  //       return user ? user?.photoURL : null;
  // }

  //   const getUserInitial = (uid) => {
  //       const user = userNames?.find (user => user?.uid === uid || user?.user === uid);
  //       return user ? user?.initial : '';
  //   }

  return (
    <Box sx={{ display: 'flex', paddingTop: 2 }}>
      <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
        <img src={gameCelebrate} alt="Celebrate" style={{ width: '272px', marginBottom: 16 }} />
        <Typography variant="h3" sx={{ textAlign: 'center' }}>
          Game Created
        </Typography>

        <Typography sx={{ textAlign: 'center' }}>
          Check back later to see who's responded!
        </Typography>

        <br /><br />

        <Button
          variant="containedNew"
          sx={{ flex: 1 }}
          onClick={() => {
            navigate(`/dashboard`);
          }}
        >
          Back to Dashboard
        </Button>
        <br /><br />
        <Button
          variant="containedNewOutline"
          sx={{
            alignItems: 'center',
            padding: '0px',
            gap: '8px',
            flex: 1,
          }}
          onClick={() => {
            navigate(`/games/${id}`);
          }}
        >
          View Game
        </Button>
      </Container>
    </Box>
  );
}

export default GameCreated;
